import React, { useState, useEffect, useRef } from "react";
import "./index.scss";
import ReactJson from "react-json-view";
import { useTheme } from "../../../../../../theme/themeContext";
import Icon from "../../../../../../components/CustomIcon/Icon";
import { tableData, dataCollectionOptions, LoadingOptions } from "../../data";
import useDataCollection from "../../../../customHooks/useDataCollection";
import AdvancedSearch from "../AdvancedSearch/index";
import { CleaningDataCollection } from "../../services/cleanDataCollection";
import { cleanFetchCollection } from "../../services/cleanFetchCollection";
import { recursiveFn } from "../../services/recursiveFn";
import NoData from "../NoData/index";
import Lottie from "react-lottie";
import DataCollectionIdResult from "../../services/DataCollectionResult";


function DataCollectionTable({
  toggleTable = "",
  toggleAdvancedSearch = "",
  setToggleAdvancedSearch = () => {},
  advancedSearchValue = {},
  setAdvancedSearchValue = () => {},
  advancedInputVisibility = {},
  setAdvancedInputVisibility = () => {},
  scrollPosition = "",
  updateScrollPosition = () => {},
  activeRow = "",
  setActiveRow = () => {},
  searchValue = "",
  setSearchValue = () => {},
  isSearching = "",
  setIsSearching = () => {},
  filteredData = [],
  setFilteredData = () => {},
  selectedColumns = [],
  handleCheckboxChange = () => {},
  onFailedDataClick = () => {},
}) {
  const { themeMode } = useTheme();
  const { data: finalDataCollection, loading } = useDataCollection(
    "NODE_ENDPOINT_GET_DATACOLLECTION",
    CleaningDataCollection
  );
  const { data: finalFailedDataCollection } = useDataCollection(
    "NODE_ENDPOINT_GET_FAILEDDATACOLLECTION",
    cleanFetchCollection
  );
  const rowRefs = useRef([]);
  const tableRef = useRef(null);
  const rowScrollPosition = useRef(0);
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const [failedDataCollectionId, setFailedDataCollectionId] = useState([]);
  const [toggleDropDown, setToggleDropdown] = useState(false);
  const [filteredDataLength, setFilteredDataLength] = useState(0);
  const [result, setResult] = useState({});

  useEffect(() => {
    if (!isSearching) {
      setFilteredData(finalDataCollection);
    }
    const dataCollectionId = finalFailedDataCollection.map(
      (data) => data?.dataCollectionId
    );
    setFailedDataCollectionId(dataCollectionId);
  }, [finalDataCollection, finalFailedDataCollection, isSearching]);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = scrollPosition;
    }
  }, [scrollPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !dropdownButtonRef.current.contains(event.target)
      ) {
        setToggleDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = scrollPosition;
    }
  });

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = rowScrollPosition.current;
    }
  }, [activeRow]);


  const handleSearch = () => {
    if (searchValue.trim() === "") {
      setFilteredData(finalDataCollection);
      setIsSearching(false);
      setFilteredDataLength(0);
    } else {
      const newFilteredData = finalDataCollection.filter((item) =>
        recursiveFn(item, searchValue)
      );
      setFilteredData(newFilteredData);
      setFilteredDataLength(newFilteredData?.length);
      setIsSearching(true);
    }
    setActiveRow(null);
  };

  const handleRowClick = async (index, dataCollectionId) => {
    const res = await DataCollectionIdResult(dataCollectionId);
    setResult(res);
    const isRowActive = index === activeRow;
    if (!isRowActive) {
      rowScrollPosition.current = tableRef.current.scrollTop;

      if (rowRefs.current[index]) {
        rowRefs.current[index].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    setActiveRow(isRowActive ? null : index);
  };

  const handleResetSearch = () => {
    setSearchValue("");
    setFilteredData(finalDataCollection);
    setIsSearching(false);
    setActiveRow(null);
  };

  const getCellContent = (row, column, rowIndex) => {
    if (column === "Collection ID") {
      const isFailed = failedDataCollectionId.includes(row?.dataCollectionId);
      return (
        <div className="collection-id-content">
          {isFailed && (
            <span
              className="failed-badge"
              onClick={() =>
                isFailed && onFailedDataClick(row?.dataCollectionId)
              }
            >
              failed
            </span>
          )}
          <span
            className={`data-collection-id ${isFailed ? "failed-cell" : ""}`}
            onClick={() => isFailed && onFailedDataClick(row?.dataCollectionId)}
          >
            {row?.dataCollectionId || "--"}
          </span>
        </div>
      );
    }
    if (column === "Real Time") {
      return (
        <div className="sync-icon">
          {row?.sync === true ? (
            <Icon
              icon="check"
              color="green"
              size={20}
              backgroundColor="black"
            />
          ) : row?.sync === false ? (
            <Icon
              icon="error"
              color="red"
              size={20}
            />
          ) : (
            "--"
          )}
        </div>
      );
    }

    switch (column) {
      case "#":
        return rowIndex + 1;
      case "Mboname":
        return row?.mboname || "--";
      case "WhereClause":
        return row?.whereclause || "--";
      case "Login":
        return row?.login || "--";
      case "Ready":
        return String(row?.ready) || "--";
      case "WhereClause ID":
        return row?.whereClauseId || "--";
      case "Result":
        return row?.result || "--";
        // case "Real Time":
        //   return row?.sync || 
        
      case "Data Hash":
        return row?.dataHash || "--";
      case "completewhereclause":
        return row?.completewhereclause || "--";
      case "mainWhereclause":
        return row?.mainWhereclause || "--";
      case "mainCompletewhereclause":
        return row?.mainCompletewhereclause || "--";
      default:
        return "--";
    }
  };

  return (
    <div>
      {loading ? (
        <Lottie
          options={LoadingOptions}
          height={100}
          width={120}
          isClickToPauseDisabled={true}
          style={{ cursor: "auto" }}
        />
      ) : (
        <div className="data-collection-container">
          {toggleAdvancedSearch[toggleTable]?.toggleFilter && (
            <AdvancedSearch
              setFilteredData={setFilteredData}
              data={finalDataCollection}
              toggleSearch={toggleTable}
              advancedSearchValue={advancedSearchValue}
              setAdvancedSearchValue={setAdvancedSearchValue}
              advancedInputVisibility={advancedInputVisibility}
              setAdvancedInputVisibility={setAdvancedInputVisibility}
              setActiveRow={setActiveRow}
            />
          )}
          <div className="monitor-Dashboard">
            <div className="search-table">
              <button
                className="advanced-filter"
                onClick={() => {
                  setToggleAdvancedSearch((prev) => ({
                    ...prev,
                    [toggleTable]: {
                      toggleFilter: !prev[toggleTable].toggleFilter,
                    },
                  }));
                }}
              >
                <Icon
                  icon="advanced-search"
                  size={22}
                  style={{
                    color: toggleAdvancedSearch[toggleTable]?.toggleFilter
                      ? "#4C36A5"
                      : "#989898",
                  }}
                />
              </button>
              <p className="search-divider"></p>
              <button className="default-search" onClick={handleSearch}>
                <Icon
                  icon={"search"}
                  size={22}
                  style={{ color: searchValue ? "#4C36A5" : "#989898" }}
                />
              </button>
              <input
                type="text"
                className="dashboard-search-input"
                placeholder={"Search"}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />

              <button className="reset-search" onClick={handleResetSearch}>
                <Icon
                  icon={isSearching && "circle-close-new"}
                  size={22}
                  style={{ color: "#C4C3C9" }}
                />
              </button>
              <p className="search-divider"></p>
              <div
                className="CollectionLength"
                style={{
                  color: isSearching ? "var(--primary)" : "var(--monitorText)",
                  width: isSearching && "auto",
                }}
              >
                <p className="Collection-sum">
                  {" "}
                  {isSearching
                    ? filteredDataLength + " / " + finalDataCollection?.length
                    : finalDataCollection?.length}
                </p>
                <sub className="Collection"> Collections</sub>
              </div>
              <p className="search-divider"></p>
              <button
                className="toggle-dropdown"
                ref={dropdownButtonRef}
                onClick={() => setToggleDropdown((prev) => !prev)}
              >
                <Icon
                  icon="dashboard-dropdown"
                  size={22}
                  style={{ color: toggleDropDown ? "#4C36A5" : "#989898" }}
                />
              </button>
              {toggleDropDown && (
                <div className="dropdown-content" ref={dropdownRef}>
                  {dataCollectionOptions.map((column, index) => (
                    <label className="dropdown-dashboard-label" key={index}>
                      <input
                        className="dropdown-input"
                        type="checkbox"
                        value={column}
                        checked={selectedColumns.includes(column)}
                        onChange={handleCheckboxChange}
                      />
                      {column}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {finalDataCollection.length > 0 ? (
              <div className="data-table-wrapper" ref={tableRef}>
                <table className="data-table">
                  <thead className="table-header">
                    <tr>
                      {selectedColumns?.map((header, index) => (
                        <th
                          className={`header-cell ${
                            header === "#" ? "row-index-header" : ""
                          }`}
                          key={index}
                          style={{
                            textAlign: header === "#" && "center",
                            paddingLeft: header === "#" && "2rem",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="tbody-wrapper">
                    {filteredData.length > 0 ? (
                      filteredData?.map((row, rowIndex) => {
                        const dataCollectionId = row?.dataCollectionId
                        const data =
                          Object.keys(result)?.length > 0
                            ? { ...row, result: result }
                            : row;
                        return (
                          <React.Fragment key={rowIndex}>
                            <>
                              <tr
                                ref={(el) => (rowRefs.current[rowIndex] = el)}
                                onClick={() =>
                                  handleRowClick(rowIndex, dataCollectionId)
                                }
                                style={{
                                  background:
                                    activeRow === rowIndex &&
                                    "var(--moniteurRow)",
                                }}
                              >
                                {selectedColumns.map((column, cellIndex) => {
                                  const cellClassName =
                                    column === "#" ? "row-index-cell" : "";
                                  return (
                                    <td
                                      key={cellIndex}
                                      className={cellClassName}
                                    >
                                      <div className="row-icon">
                                        {column === "#" && (
                                          <Icon
                                            icon={
                                              activeRow === rowIndex
                                                ? "arrow-up"
                                                : "arrow-down"
                                            }
                                            size={16}
                                            color={
                                              activeRow !== rowIndex
                                                ? "#DADADA"
                                                : "var(--primary)"
                                            }
                                          />
                                        )}
                                        {getCellContent(row, column, rowIndex)}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                              {activeRow === rowIndex && (
                                <tr>
                                  <td
                                    colSpan={selectedColumns.length}
                                    className="full-width-row"
                                  >
                                    <ReactJson
                                      src={data}
                                      theme={
                                        themeMode === "light"
                                          ? "rjv-default"
                                          : "monokai"
                                      }
                                      collapsed={false}
                                      enableClipboard={true}
                                      displayObjectSize={true}
                                      displayDataTypes={true}
                                      name={false}
                                      style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-all",
                                      }}
                                    />
                                  </td>
                                </tr>
                              )}
                            </>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <NoData
                        data1={
                          isSearching
                            ? "No results found."
                            : "No Data to Display."
                        }
                        data2={isSearching ? "Please try another search." : ""}
                      />
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <NoData data1={"No data available."} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataCollectionTable;
